import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDrawerOpen } from '../../store/global';
import { ItemShort } from '../item/item.Short';
import { fetchImage } from '../../utils/img';
import { getAvatarBackgroundStyle, getItemPicAsset } from '../../utils/item';
import { renderIconBySlug } from '../../content/icons';

export const LinkFull = props => {
  const { linkId, getTextHeight, fieldId, parentItemId, fullTabId, gridLayoutKey } = props;

  const link = useSelector(state => state.items[linkId]);
  const tab = useSelector(state => state.items[parentItemId]);
  const dispatch = useDispatch();

  const wrapperRef = useRef(null);
  const contentRef = useRef(null);

  const [avatar, setAvatar] = useState('');
  const [avatarIconSlug, setAvatarIconSlug] = useState(null);
  const [avatarBgColor, setAvatarBgColor] = useState(null);
  const fullTabOverride = useMemo(() => {
    if (fullTabId) {
      return fullTabId;
    }
    if (!tab?.grid || !fieldId) {
      return 'sidebar';
    }
    const entry = tab.grid.find(element => element[fieldId]);
    return entry?.[fieldId]?.fullTabId || 'sidebar';
  }, [fullTabId, tab, fieldId]);

  useEffect(() => {
    const avatarAsset = getItemPicAsset(link, 0);
    if (avatarAsset.url) {
      fetchImage(avatarAsset.url, setAvatar);
    } else {
      setAvatar('');
    }
    setAvatarIconSlug(avatarAsset.iconSlug);
    setAvatarBgColor(avatarAsset.bgColor ?? null);
  }, [link]);

  useEffect(() => {
    if (!getTextHeight || !fieldId || !parentItemId || !wrapperRef?.current) {
      return;
    }

    const notifyHeight = () => {
      if (wrapperRef.current) {
        getTextHeight(
          wrapperRef.current.clientHeight,
          fieldId,
          parentItemId,
          'link',
          gridLayoutKey
        );
      }
    };

    notifyHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const target = contentRef.current ?? wrapperRef.current;
    if (!target) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        if (entry.target === target) {
          notifyHeight();
        }
      }
    });

    resizeObserver.observe(target);

    return () => {
      resizeObserver.disconnect();
    };
  }, [getTextHeight, fieldId, parentItemId, gridLayoutKey]);

  return (
    <div
      ref={wrapperRef}
      className={`mt-1 ${getTextHeight ? '' : 'h-full'}`}
    >
      <div
        className='flex gap-1 px-1 cursor-pointer'
        onClick={() => dispatch(setDrawerOpen(linkId))}
      >
        <div
          className='rounded-full shadow flex items-center justify-center w-2 h-2'
          style={{
            ...(avatarBgColor
              ? getAvatarBackgroundStyle(avatarBgColor)
              : { backgroundColor: 'rgba(var(--main), 0.5)' }),
          }}
        >
          {avatarIconSlug
            ? renderIconBySlug(avatarIconSlug, {
                size: 32,
                color: 'var(--contrast)',
              })
            : avatar && (
                <div
                  className='w-full h-full rounded-full'
                  style={{
                    backgroundImage: `url(${avatar})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                  }}
                ></div>
              )}
        </div>
        <div className='font-condensed font-bold text-lg'>{link?.label}</div>
      </div>
      <div
        className={`pl-1 ml-08 -mt-05 ${
          getTextHeight ? '' : 'h-full overflow-y-auto'
        }`}
      >
        <div
          ref={contentRef}
          className='border-contrast border-opacity-10 border-l'
        >
          <ItemShort
            itemId={linkId}
            displayType={'card'}
            refContainer={contentRef}
            height='auto'
            sidebarTabOverride={fullTabOverride}
          />
        </div>
      </div>
    </div>
  );
};
